import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import { Json } from '@/integrations/supabase/types';

export interface FeedbackAttachment {
  name: string;
  url: string;
  type: string;
  size: number;
}

export interface FeedbackReply {
  id: string;
  feedback_id: string;
  author_id: string;
  author_name: string;
  author_role: 'client' | 'team_member' | 'event_planner';
  message: string;
  attachments: FeedbackAttachment[];
  created_at: string;
}

interface UseFeedbackThreadOptions {
  feedbackId: string;
  userRole: 'client' | 'team_member' | 'event_planner';
  userName?: string;
}

const parseAttachments = (attachments: Json | null): FeedbackAttachment[] => {
  if (!attachments || !Array.isArray(attachments)) return [];
  const result: FeedbackAttachment[] = [];
  for (const att of attachments) {
    if (typeof att === 'object' && att !== null && 
        'name' in att && 'url' in att && 'type' in att && 'size' in att) {
      result.push({
        name: String((att as Record<string, unknown>).name),
        url: String((att as Record<string, unknown>).url),
        type: String((att as Record<string, unknown>).type),
        size: Number((att as Record<string, unknown>).size)
      });
    }
  }
  return result;
};

export const useFeedbackThread = ({ feedbackId, userRole, userName }: UseFeedbackThreadOptions) => {
  const { user } = useAuth();
  const [replies, setReplies] = useState<FeedbackReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const loadReplies = async () => {
    if (!feedbackId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('feedback_replies')
        .select('*')
        .eq('feedback_id', feedbackId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      // Parse attachments from JSON
      const parsedReplies: FeedbackReply[] = (data || []).map(reply => ({
        id: reply.id,
        feedback_id: reply.feedback_id,
        author_id: reply.author_id,
        author_name: reply.author_name,
        author_role: reply.author_role as 'client' | 'team_member' | 'event_planner',
        message: reply.message,
        created_at: reply.created_at,
        attachments: parseAttachments(reply.attachments)
      }));
      
      setReplies(parsedReplies);
    } catch (error) {
      console.error('Error loading replies:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReplies();
  }, [feedbackId]);

  // Real-time subscription for new replies
  useEffect(() => {
    if (!feedbackId) return;

    const channel = supabase
      .channel(`feedback-replies-${feedbackId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'feedback_replies',
          filter: `feedback_id=eq.${feedbackId}`
        },
        (payload) => {
          const raw = payload.new as Record<string, unknown>;
          const newReply: FeedbackReply = {
            id: raw.id as string,
            feedback_id: raw.feedback_id as string,
            author_id: raw.author_id as string,
            author_name: raw.author_name as string,
            author_role: raw.author_role as 'client' | 'team_member' | 'event_planner',
            message: raw.message as string,
            created_at: raw.created_at as string,
            attachments: parseAttachments(raw.attachments as Json)
          };
          setReplies(prev => {
            // Avoid duplicates
            if (prev.find(r => r.id === newReply.id)) return prev;
            return [...prev, newReply];
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [feedbackId]);

  const uploadAttachment = async (file: File): Promise<FeedbackAttachment | null> => {
    if (!user?.id) return null;

    try {
      setUploading(true);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('feedback-attachments')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('feedback-attachments')
        .getPublicUrl(fileName);

      return {
        name: file.name,
        url: publicUrl,
        type: file.type,
        size: file.size
      };
    } catch (error) {
      console.error('Error uploading attachment:', error);
      toast.error('Failed to upload attachment');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const sendNotification = async (message: string, authorName: string) => {
    try {
      await supabase.functions.invoke('send-feedback-reply-notification', {
        body: {
          feedbackId,
          replyMessage: message,
          authorName,
          authorRole: userRole
        }
      });
    } catch (error) {
      console.error('Failed to send notification:', error);
      // Don't throw - notification failure shouldn't block the reply
    }
  };

  const addReply = async (message: string, attachments: FeedbackAttachment[] = []) => {
    if (!user?.id || (!message.trim() && attachments.length === 0)) return;

    const authorName = userName || user.user_metadata?.display_name || user.email?.split('@')[0] || 'User';

    try {
      const { data, error } = await supabase
        .from('feedback_replies')
        .insert({
          feedback_id: feedbackId,
          author_id: user.id,
          author_name: authorName,
          author_role: userRole,
          message: message.trim(),
          attachments: attachments as unknown as Json
        })
        .select()
        .single();

      if (error) throw error;

      // Send email notifications to other participants
      sendNotification(message.trim(), authorName);

      // Real-time will add it, but for immediate feedback we add it optimistically
      const newReply: FeedbackReply = {
        id: data.id,
        feedback_id: data.feedback_id,
        author_id: data.author_id,
        author_name: data.author_name,
        author_role: data.author_role as 'client' | 'team_member' | 'event_planner',
        message: data.message,
        created_at: data.created_at,
        attachments: parseAttachments(data.attachments)
      };
      
      setReplies(prev => {
        if (prev.find(r => r.id === newReply.id)) return prev;
        return [...prev, newReply];
      });

      return data;
    } catch (error) {
      console.error('Error adding reply:', error);
      toast.error('Failed to send reply');
      throw error;
    }
  };

  return {
    replies,
    loading,
    uploading,
    addReply,
    uploadAttachment,
    refetch: loadReplies
  };
};
